import React, { useState, useEffect, useRef, useMemo } from 'react';
import PHQ9Component from './PHQ9Component';
import ConsentTab from './tabs/ConsentTab';
import GAD7Tab from './tabs/GAD7Tab';
import WSASTab from './tabs/WSASTab';
import RiskTab from './tabs/RiskTab';
import AssessmentTabTriage from './tabs/AssessmentTabTriage';
import WorkHistoryTab from './tabs/WorkHistoryTab';
import ClinicalNotesTab from './tabs/ClinicalNotesTab';
import RCADS47Component from './RCADS47Component';
import YPCoreComponent from './YPCoreComponent';
import './TabbedReportForm.css';
import AssessmentTabNetworkEform from './tabs/AssessmentTabNetworkEform';
import ProblemDescriptorTab from './tabs/ProblemDescriptorTab';
import TreatmentGoalsTab from './tabs/TreatmentGoalsTab';
import ClinicalCheckTab from './tabs/ClinicalCheckTab';
import ClinicalNotesTriageFollowUpTab from './tabs/ClinicalNotesTriageFollowUpTab';
import TreatmentRecommendationsEformTab from './tabs/TreatmentRecommendationsEformTab';
import WorkAndGoalsEformTab from './tabs/WorkAndGoalsEformTab';
import TreatmentPathwaysTriageFollowUpTab from './tabs/TreatmentPathwaysTriageFollowUpTab';
import TreatmentGoalsProblemDescriptorTriageFollowUpTab from './tabs/TreatmentGoalsProblemDescriptorTriageFollowUpTab';
import { generatePdfFromTabs } from './pdfGenerator';
import TabStatusIndicator from './TabStatusIndicator';
import CloseCaseDialog from './CloseCaseDialog';

// Placeholder component for unsupported sections
const UnsupportedTabComponent = ({ sectionName }) => {
  return (
    <div className="unsupported-tab-content">
      <div className="unsupported-warning-icon">⚠</div>
      <h2>Unsupported Section</h2>
      <p className="unsupported-message">
        Tab <strong>{sectionName}</strong> is not supported in frontend but requested by backend.
      </p>
      <p className="unsupported-help">
        Please contact your system administrator to resolve this configuration issue.
      </p>
    </div>
  );
};

const TabbedReportForm = ({ reportId, reportIntId, caseId, api }) => {
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [availableSections, setAvailableSections] = useState(null);
  const [loadingSections, setLoadingSections] = useState(true);
  const [sectionsError, setSectionsError] = useState(null);
  const [reportType, setReportType] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [pdfProgress, setPdfProgress] = useState('');
  const [sectionCompletions, setSectionCompletions] = useState({});
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [publishError, setPublishError] = useState(null);
  const [isPublished, setIsPublished] = useState(false);
  const [showCloseCaseDialog, setShowCloseCaseDialog] = useState(false);
  const [casesToClose, setCasesToClose] = useState([]);
  const tabRefs = useRef([]);
  const tabContentRef = useRef(null);

  // Format date from ISO string to DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Ticket 2380: On a published report the active tab is rendered inside a
  // `<fieldset disabled>`, which turns every textarea into a disabled control.
  // Disabled textareas can't be focused or scrolled, so any content taller than
  // their fixed `rows` height is clipped and unreadable (this is why the PDF,
  // which renders the tabs un-disabled, shows everything but the screen doesn't).
  // To match the PDF, grow each published textarea to fit its content so all
  // text is visible without needing to scroll. scrollHeight is still readable on
  // a disabled element, so this works without changing the read-only behaviour.
  useEffect(() => {
    const container = tabContentRef.current;
    if (!isPublished || !container) {
      return undefined;
    }

    const growTextareas = () => {
      container.querySelectorAll('textarea').forEach((el) => {
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      });
    };

    // Run once now and again whenever the tab's content/values change (tabs load
    // their data asynchronously, so the text often arrives after first render).
    growTextareas();
    const observer = new MutationObserver(growTextareas);
    observer.observe(container, { childList: true, subtree: true, characterData: true });

    return () => observer.disconnect();
  }, [isPublished, currentTabIndex]);

  // All possible tabs with their section mappings
  const allTabs = [
    { id: 'psych-eform-confirmation', label: 'Confirmation', component: ConsentTab, section: 'psych-eform-confirmation' },
    { id: 'psych-triage-consent', label: 'Consent & Patient Overview', component: ConsentTab, section: 'psych-triage-consent' },
    { id: 'psych-triage-followup-consent', label: 'Consent & Patient Overview', component: ConsentTab, section: 'psych-triage-followup-consent' },
    { id: 'psych-eform-initial-assessment', label: 'Assessment', component: AssessmentTabNetworkEform, section: 'psych-eform-initial-assessment' },
    { id: 'psych-triage-assessment-notes', label: 'Assessment Notes', component: AssessmentTabTriage, section: 'psych-triage-assessment-notes' },
    { id: 'psych-triage-problem-descriptor', label: 'Problem Descriptor', component: ProblemDescriptorTab, section: 'psych-triage-problem-descriptor' },
    { id: 'psych-triage-treatment-goals', label: 'Treatment Goals', component: TreatmentGoalsTab, section: 'psych-triage-treatment-goals' },
    { id: 'psych-triage-followup-treatment-goals-problem-descriptor', label: 'Goals and Problem Descriptor', component: TreatmentGoalsProblemDescriptorTriageFollowUpTab, section: 'psych-triage-followup-treatment-goals-problem-descriptor' },
    { id: 'psych-triage-work-history', label: 'Work History', component: WorkHistoryTab, section: 'psych-triage-work-history' },
    { id: 'psych-eform-work-and-goals', label: 'Work & Goals', component: WorkAndGoalsEformTab, section: 'psych-eform-work-and-goals' },
    { id: 'psych-triage-followup-clinical-notes', label: 'Clinical Notes', component: ClinicalNotesTriageFollowUpTab, section: 'psych-triage-followup-clinical-notes' },
    { id: 'psych-triage-followup-treatment-pathways', label: 'Treatment Pathways', component: TreatmentPathwaysTriageFollowUpTab, section: 'psych-triage-followup-treatment-pathways' },
    { id: 'psych-eform-treatment-recommendations', label: 'Treatment & Recommendations', component: TreatmentRecommendationsEformTab, section: 'psych-eform-treatment-recommendations' },
    { id: 'psych-triage-followup-clinical-check', label: 'Clinical Check', component: ClinicalCheckTab, section: 'psych-triage-followup-clinical-check' },
    { id: 'psych-eform-initial-clinical-check', label: 'Clinical Check', component: ClinicalCheckTab, section: 'psych-eform-initial-clinical-check' },
    { id: 'psych-eform-followup-clinical-check', label: 'Clinical Check', component: ClinicalCheckTab, section: 'psych-eform-followup-clinical-check' },
    { id: 'ypcore', label: 'YP-CORE', component: YPCoreComponent, section: 'ypcore' },
    { id: 'rcads47', label: 'RCADS (Child)', component: RCADS47Component, section: 'rcads47' },
    { id: 'rcads47parent', label: 'RCADS (Parent)', component: RCADS47Component, section: 'rcads47parent' },
    { id: 'phq9', label: 'PHQ-9', component: PHQ9Component, section: 'PHQ9Assessment' },
    { id: 'gad7', label: 'GAD-7', component: GAD7Tab, section: 'GAD7Assessment' },
    { id: 'wsas', label: 'WSAS', component: WSASTab, section: 'WSASAssessment' },
    { id: 'psych-triage-risk', label: 'Risk', component: RiskTab, section: 'psych-triage-risk' },
    { id: 'clinical-notes', label: 'Clinical Notes', component: ClinicalNotesTab, section: 'ClinicalNotes' }
  ];

  // Create lookup map for efficient section-to-tab matching (case-insensitive)
  const tabMap = useMemo(() => new Map(allTabs.map(tab => [tab.section.toLowerCase(), tab])), [allTabs]);

  // Fetch available sections and report type from API
  useEffect(() => {
    const fetchSections = async () => {
      try {
        setLoadingSections(true);
        
        // Fetch report info to get reportTypeId
        const reportResponse = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}`);
        if (reportResponse.ok) {
          const reportData = await reportResponse.json();
          setReportType(reportData.reportTypeId);
          if (reportData.reportStatus === 4) {
            setIsPublished(true);
          }
        }
        
        // Fetch available sections
        const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections`);

        if (response.ok) {
          const data = await response.json();
          let sections = data.sections || [];

          setAvailableSections(sections);
        } else {
          setSectionsError(`Failed to load sections: ${response.status}`);
        }
      } catch (error) {
        console.error('Error loading sections:', error);
        setSectionsError(error.message);
      } finally {
        setLoadingSections(false);
      }
    };

    if (reportId) {
      fetchSections();
    }
  }, [reportId, api]);

  // Fetch completion percentage for each section
  useEffect(() => {
    const fetchSectionCompletions = async () => {
      if (!availableSections || availableSections.length === 0) return;

      const completions = {};

      for (const sectionName of availableSections) {

        const existingTab = tabMap.get(sectionName.toLowerCase());
        if (!existingTab) {
          // Unsupported section, skip fetching completion
          completions[sectionName] = 0;
          continue;
        }

        const sectionKey = existingTab.section;

        try {
          const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/${sectionKey}`);
          if (response.ok) {
            const data = await response.json();
            completions[sectionKey] = data.completionPercentage || 0;
          }
        } catch (error) {
          console.error(`Error fetching completion for ${sectionName}:`, error);
          completions[sectionKey] = 0;
        }
      }

      setSectionCompletions(completions);
    };

    if (reportId && availableSections) {
      fetchSectionCompletions();
    }
  }, [reportId, availableSections, api]);

  useEffect(() => {
    const fetchPatientInfo = async () => {
        try {
            const response = await api.webAPIFetch(`/api/PatientCaseReports/CaseDetails/${caseId}`);
            if (response.ok) {
              const data = await response.json();

              // Transform the API response structure to match UI expectations
              const transformedData = {
                caseId: data.case?.caseID,
                caseRef: data.case?.caseRef,
                name: `${data.patient?.firstName || ''} ${data.patient?.lastName || ''}`.trim(),
                dateOfBirth: data.patient?.birthDate ? formatDate(data.patient.birthDate) : '',
                address: data.patient?.address || '',
                ageAtReferral: data.patient?.ageAtReferral,
                ageAtToday: data.patient?.ageAtToday,
                isCYPApplicable: data.patient?.isCYPApplicable,
                isChildAtReferral: data.patient?.isChildAtReferral,
                isChildToday: data.patient?.isChildToday,
                clientID: data.case?.clientID,
              };

              setPatientInfo(transformedData);
            } else {
              console.error('CaseDetails API failed:', response.status, response.statusText);
            }
        } catch (error) {
            console.error('Error loading patient info:', error);
        }
    };

    if (caseId) {
      fetchPatientInfo();
    }
  }, [caseId, api]);

  // Order tabs based on backend sections and create placeholders for unsupported ones
  const tabs = availableSections
    ? (() => {
        
        // Map backend sections to tabs in the order provided by backend
        return availableSections.map(sectionName => {
          // Use case-insensitive comparison for section matching
          const existingTab = tabMap.get(sectionName.toLowerCase());
          
          if (existingTab) {
            return existingTab;
          } else {
            // Create placeholder for unsupported section
            return {
              id: `unsupported-${sectionName.toLowerCase()}`,
              label: `⚠ ${sectionName}`,
              component: ({ sectionName: name }) => <UnsupportedTabComponent sectionName={name || sectionName} />,
              section: sectionName,
              isUnsupported: true
            };
          }
        });
      })()
    : allTabs;

  // Refresh completion for a specific tab section
  const refreshTabCompletion = async (tabIndex) => {
    const tab = tabs[tabIndex];
    if (tab && tab.section && !tab.isUnsupported) {
      try {
        const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/${tab.section}`);
        if (response.ok) {
          const data = await response.json();
          setSectionCompletions(prev => ({
            ...prev,
            [tab.section]: data.completionPercentage || 0
          }));
        }
      } catch (error) {
        console.error(`Error refreshing completion for ${tab.section}:`, error);
      }
    }
  };

  const handleTabClick = (index) => {
    refreshTabCompletion(currentTabIndex);
    setCurrentTabIndex(index);
  };

  const handlePrevious = () => {
    if (currentTabIndex > 0) {
      refreshTabCompletion(currentTabIndex);
      setCurrentTabIndex(currentTabIndex - 1);
    }
  };

  const handleNext = e => {
    e?.preventDefault();
    if (currentTabIndex < tabs.length - 1) {
      refreshTabCompletion(currentTabIndex);
      setCurrentTabIndex(currentTabIndex + 1);
    }
  };

  const handleSave = async () => {
    // Auto-save is handled by individual components
    // This just updates the timestamp
    setLastSaveTime(new Date());

    // Refresh completion percentage for current tab
    const currentTab = tabs[currentTabIndex];
    if (currentTab && currentTab.section) {
      const sectionKey = currentTab.section;
      try {
        const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/${sectionKey}`);
        if (response.ok) {
          const data = await response.json();
          setSectionCompletions(prev => ({
            ...prev,
            [sectionKey]: data.completionPercentage || 0
          }));
        }
      } catch (error) {
        console.error(`Error refreshing completion for ${sectionKey}:`, error);
      }
    }
  };

  // Get tab status based on completion percentage
  const getTabStatus = (sectionName) => {
    const completion = sectionCompletions[sectionName];
    if (completion === undefined) return 'not-started';
    if (completion === 100) return 'complete';
    if (completion === 0) return 'not-started';
    return 'incomplete';
  };

  // Check if all supported sections are complete
  const allSectionsComplete = useMemo(() => {
    if (!availableSections || availableSections.length === 0) return false;

    const supportedTabs = tabs.filter(tab => !tab.isUnsupported);
    if (supportedTabs.length === 0) return false;

    return supportedTabs.every(tab => sectionCompletions[tab.section] === 100);
  }, [tabs, sectionCompletions, availableSections]);

  const handlePublishReport = async (e) => {
    e?.preventDefault(); // prevent form submission in Pharos

    try {
      setIsPublishing(true);
      setPublishError(null);
      setPublishSuccess(false);
      setPdfProgress('Preparing tabs for PDF generation...');

      // Wait for all tabs to finish loading (no more spinners)
      const maxWait = 60000; // 60 second timeout
      const pollInterval = 500;
      let waited = 0;

      // Wait for refs to be populated and at least one tab to start loading
      await new Promise(resolve => setTimeout(resolve, 2000));

      // First, verify we actually see spinners (tabs have mounted and started loading)
      const initialHasSpinners = tabRefs.current.some(
        el => el && el.querySelector('.loading-state .spinner')
      );

      if (initialHasSpinners) {
        // Poll until all spinners are gone
        while (waited < maxWait) {
          const hasSpinners = tabRefs.current.some(
            el => el && el.querySelector('.loading-state .spinner')
          );
          if (!hasSpinners) break;
          await new Promise(resolve => setTimeout(resolve, pollInterval));
          waited += pollInterval;
          setPdfProgress(`Waiting for tabs to load... (${Math.round(waited / 1000)}s)`);
        }

        // If we timed out, abort the publish
        if (waited >= maxWait) {
          throw new Error('Timed out waiting for all tabs to finish loading. Please try again.');
        }
      }

      // Extra settle time for any final renders after data loads
      await new Promise(resolve => setTimeout(resolve, 1000));

      setPdfProgress('Capturing tabs...');

      // Collect all tab elements with their labels
      const tabsToCapture = tabs
        .map((tab, index) => ({
          element: tabRefs.current[index],
          label: tab.label
        }))
        .filter(t => t.element); // Only include tabs that were successfully rendered

      if (tabsToCapture.length === 0) {
        throw new Error('No tabs available to capture for PDF');
      }

      setPdfProgress(`Generating PDF with ${tabsToCapture.length} tabs...`);

      // Generate the PDF without password protection, as base64
      const fileName = `Report_${patientInfo?.caseId || caseId}_${new Date().toISOString().split('T')[0]}.pdf`;
      const result = await generatePdfFromTabs(tabsToCapture, fileName, patientInfo, null, true);

      if (!result.success || !result.base64) {
        throw new Error('Failed to generate PDF');
      }

      setPdfProgress('Publishing report with PDF...');

      // Send the base64 PDF in the POST body
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          B64PDFDoc: result.base64
        })
      });

      if (response.ok) {
        setIsPublished(true);
        setPdfProgress('');

        // For triage reports, check if closeCase was set in the section data
        const isTriageReport = reportType === 2002 || reportType === 2003;
        if (isTriageReport) {
          try {
            // CloseCase lives in different sections depending on report type
            const closeCaseSection = reportType === 2002
              ? 'psych-triage-treatment-goals'
              : 'psych-triage-followup-treatment-pathways';

            const sectionResponse = await api.webAPIFetch(
              `/api/PatientCaseReports/${reportId}/sections/${closeCaseSection}`
            );

            if (sectionResponse.ok) {
              const sectionData = await sectionResponse.json();
              if (sectionData.closeCase === true) {
                // Build a minimal case object for the dialog
                setCasesToClose([{
                  pkCaseID: caseId,
                  CaseID: patientInfo?.caseRef || caseId,
                  CloseCase: true,
                  Active: true
                }]);
                setShowCloseCaseDialog(true);
              } else {
                setPublishSuccess(true);
                setTimeout(() => setPublishSuccess(false), 3000);
              }
            } else {
              setPublishSuccess(true);
              setTimeout(() => setPublishSuccess(false), 3000);
            }
          } catch (sectionErr) {
            console.error('Error checking closeCase:', sectionErr);
            setPublishSuccess(true);
            setTimeout(() => setPublishSuccess(false), 3000);
          }
        } else {
          setPublishSuccess(true);
          setTimeout(() => setPublishSuccess(false), 3000);
        }
      } else {
        let errorMessage = `Failed to publish report: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.title || errorMessage;
        } catch (e) {
          // If JSON parsing fails, try to get text
          const errorText = await response.text();
          if (errorText) {
            errorMessage = errorText;
          }
        }
        setPublishError(errorMessage);
        setPdfProgress('');
        // Clear error after 5 seconds
        setTimeout(() => {
          setPublishError(null);
        }, 5000);
      }
    } catch (error) {
      console.error('Error publishing report:', error);
      setPublishError(error.message);
      setPdfProgress('');
      // Clear error after 5 seconds
      setTimeout(() => {
        setPublishError(null);
      }, 5000);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCloseCaseComplete = () => {
    setShowCloseCaseDialog(false);
    setCasesToClose([]);
    setPublishSuccess(true);
    setTimeout(() => setPublishSuccess(false), 3000);
  };

  const handleCloseCaseSkip = () => {
    setShowCloseCaseDialog(false);
    setCasesToClose([]);
    setPublishSuccess(true);
    setTimeout(() => setPublishSuccess(false), 3000);
  };

  const CurrentTabComponent = tabs[currentTabIndex]?.component;

  // Loading state
  if (loadingSections) {
    return (
      <div className="tabbed-report-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading report sections...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (sectionsError) {
    return (
      <div className="tabbed-report-container">
        <div className="error-state">
          <h2>Error Loading Sections</h2>
          <p>{sectionsError}</p>
        </div>
      </div>
    );
  }

  // No tabs available
  if (tabs.length === 0) {
    return (
      <div className="tabbed-report-container">
        <div className="error-state">
          <h2>No Sections Available</h2>
          <p>This report has no sections configured.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tabbed-report-container">
      {/* Report Header */}
      <div className="report-header">
        <div className="report-info">
          {patientInfo && (
            <>
              <div className="patient-info-item">
                <strong>Case Ref:</strong> {patientInfo.caseRef || caseId}
              </div>
              <div className="patient-info-item">
                <strong>Case ID:</strong> {patientInfo.caseId}
              </div>
              <div className="patient-info-item">
                <strong>Name:</strong> {patientInfo.name}
              </div>
              <div className="patient-info-item">
                <strong>DOB:</strong> {patientInfo.dateOfBirth}
              </div>
              {patientInfo.address && (
                <div className="patient-info-item">
                  <strong>Address:</strong> {patientInfo.address}
                </div>
              )}
              <div className="patient-info-item">
                <strong>Age at Referral (years):</strong> {patientInfo.ageAtReferral}
              </div>
              <div className="patient-info-item">
                <strong>Age Today (years):</strong> {patientInfo.ageAtToday}
              </div>
              <div className="patient-info-item">
                <strong>Is CYP Applicable:</strong> {patientInfo.isCYPApplicable ? 'Yes' : 'No'}
              </div>
            </>
          )}
        </div>
        <div className="report-actions">
          {isPublished ? (
            <div className="published-banner">
              This report has been published and cannot be edited.
            </div>
          ) : (
            <>
              <span title={!allSectionsComplete && !isPublishing ? 'Complete all sections before publishing' : ''}>
                <button
                  className="publish-button"
                  onClick={handlePublishReport}
                  disabled={isPublishing || !allSectionsComplete}
                >
                  {isPublishing ? 'Publishing...' : 'Publish Report'}
                </button>
              </span>
              {!allSectionsComplete && !isPublishing && (
                <div className="publish-incomplete-message">
                  All sections must be complete before publishing.
                </div>
              )}
              {publishSuccess && (
                <div className="publish-success-message">
                  Report published successfully!
                </div>
              )}
              {publishError && (
                <div className="publish-error-message">
                  Error: {publishError}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tabs-navigation">
        <ul className="tabs-list" role="tablist">
          {tabs.map((tab, index) => {
            const status = tab.isUnsupported ? 'not-started' : getTabStatus(tab.section);
            return (
              <li
                key={tab.id}
                className={`tab-item ${index === currentTabIndex ? 'active' : ''} ${tab.isUnsupported ? 'unsupported-tab' : ''}`}
                role="tab"
                aria-selected={index === currentTabIndex}
                onClick={() => handleTabClick(index)}
              >
                <a href={`#${tab.id}`} onClick={(e) => e.preventDefault()}>
                  <TabStatusIndicator status={status} label={tab.label} />
                </a>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Tab Content */}
      <div className="tab-content" ref={tabContentRef}>
        <div className="tab-panel active" role="tabpanel">
          <CurrentTabComponent
            reportId={reportId}
            caseId={caseId}
            patientInfo={patientInfo}
            api={api}
            onSave={handleSave}
            sectionName={tabs[currentTabIndex]?.section}
            reportType={['consent', 'assessment-notes'].includes(tabs[currentTabIndex]?.id) ? reportType : undefined}
            isPublished={isPublished}
          />
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="tab-navigation-controls">
        <button
          className="nav-button previous"
          onClick={handlePrevious}
          disabled={currentTabIndex === 0}
        >
          ← Previous
        </button>

        <div className="tab-progress">
          Tab {currentTabIndex + 1} of {tabs.length}
        </div>

        <button
          className="nav-button next"
          onClick={handleNext}
          disabled={currentTabIndex === tabs.length - 1}
        >
          Next →
        </button>
      </div>

      {/* Hidden container for rendering all tabs during PDF generation */}
      {isPublishing && (
        <div className="pdf-capture-container" style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          {tabs.map((tab, index) => {
            const TabComponent = tab.component;
            return (
              <div
                key={tab.id}
                ref={(el) => (tabRefs.current[index] = el)}
                className="pdf-tab-capture"
                style={{ width: '794px', padding: '20px', backgroundColor: 'white' }}
              >
                <TabComponent
                  reportId={reportId}
                  caseId={caseId}
                  patientInfo={patientInfo}
                  api={api}
                  onSave={handleSave}
                  sectionName={tab.section}
                  reportType={['consent', 'assessment-notes'].includes(tab.id) ? reportType : undefined}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Publishing Loading Overlay */}
      {isPublishing && pdfProgress && (
        <div className="pdf-loading-overlay">
          <div className="pdf-loading-content">
            <div className="spinner"></div>
            <h3>Publishing Report</h3>
            <p>{pdfProgress}</p>
          </div>
        </div>
      )}

      {/* Close Case Dialog - shown after triage publish when case needs closure */}
      {showCloseCaseDialog && (
        <CloseCaseDialog
          api={api}
          casesToClose={casesToClose}
          onComplete={handleCloseCaseComplete}
          onSkip={handleCloseCaseSkip}
        />
      )}
    </div>
  );
};

export default TabbedReportForm;
